import { useEffect, useState } from 'react';
import { ChevronDown, Coins, CreditCard, FileText, IndianRupee, Pencil, PiggyBank, Plus, Wallet } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { StatusBadge, type Status } from '@/components/StatusBadge';
import { SideDrawer } from '@/components/drawer/SideDrawer';
import { DrawerSection } from '@/components/drawer/DrawerSection';
import { SummaryStrip } from '@/components/drawer/SummaryStrip';
import { HistoryCard } from '@/components/history/HistoryCard';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PolicyLink } from '@/components/links/RecordLinks';
import { usePolicyPaymentsQuery } from '@/api/query/payment';
import { usePolicyQuery } from '@/api/query/policy';
import { useDrawers } from '@/contexts/DrawerContext';
import type { Member, MemberBalanceSummary, Policy } from '@/types';
import { formatCurrency, formatPaymentDate, paymentTypeLabel, formatPaymentPeriod } from '@/lib/payments';
import { addMonthsToKey, formatMonthKey } from '@/lib/months';
import { canPayPolicy, getPolicyEndMonth, getPolicyProgress } from '@/lib/policies';
import { cn, memberHeading } from '@/lib/utils';
import { SOFT_BUTTON_CLASS } from '@/lib/styles';

interface PolicyDetailsProps {
  member: Member | null;
  policies: Policy[];
  balance?: MemberBalanceSummary;
  /** Policy to open first, e.g. the one clicked in the table. */
  initialPolicyId?: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}


/** Policy Details drawer: a member's policies, their totals, balance and the selected policy's payments. */
export function PolicyDetails({ member, policies, balance, initialPolicyId, open, onOpenChange }: PolicyDetailsProps) {
  const { openDrawer } = useDrawers();
  const [activeId, setActiveId] = useState<string | null>(null);

  useEffect(() => {
    if (open) setActiveId(initialPolicyId ?? policies[0]?.id ?? null);
  }, [open, initialPolicyId, policies]);

  const { data: activePolicy } = usePolicyQuery(activeId, open);
  const { data: payments = [], isLoading: paymentsLoading } = usePolicyPaymentsQuery(activeId, open);

  const livePolicies = policies.filter((policy) => policy.status !== 'closed');
  const totals = {
    paid: policies.reduce((sum, policy) => sum + policy.totalPaid, 0),
    pending: livePolicies.reduce((sum, policy) => sum + policy.amountDue, 0),
    loanOutstanding: policies.reduce((sum, policy) => sum + (policy.loan.outstanding ?? 0), 0),
    paidThisMonth: livePolicies.filter((policy) => policy.thisMonth === 'paid' || policy.thisMonth === 'no_dues').length,
  };
  const activePolicyNo = policies.find((policy) => policy.id === activeId)?.policyNo;
  const openFor = (kind: 'policyPayment' | 'policyLoan' | 'policySavings' | 'editPolicy', policy: Policy) =>
    member && openDrawer(kind, member, policy.id);

  return (
    <SideDrawer open={open} onOpenChange={onOpenChange} title="Policy Details" member={member}>
      <DrawerSection
        icon={FileText}
        title="Policies"
        count={policies.length}
        description="Click a policy to see its details"
        padded={false}
        aside={
          member && (
            <Button type="button" variant="outline" size="sm" className={SOFT_BUTTON_CLASS} onClick={() => openDrawer('addPolicy', member)}>
              <Plus className="h-3.5 w-3.5" />
              Add Policy
            </Button>
          )
        }
      >
        <div className="space-y-2.5 p-4">
          {policies.map((policy) => {
            const isActive = policy.id === activeId;
            const progress = getPolicyProgress(policy);
            const details = isActive ? activePolicy : undefined;

            return (
              <div
                key={policy.id}
                className={cn(
                  'overflow-hidden rounded-lg border bg-white',
                  isActive ? 'border-[#4369a0]/35 ring-1 ring-[#4369a0]/10' : 'border-gray-200'
                )}
              >
                <button
                  type="button"
                  onClick={() => setActiveId(isActive ? null : policy.id)}
                  aria-expanded={isActive}
                  className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-gray-50"
                >
                  <div className="min-w-0 flex-1 space-y-1.5">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-semibold text-gray-900">{policy.policyNo}</span>
                      <span className="text-xs text-gray-500">Started {formatMonthKey(policy.startMonth)}</span>
                      <StatusBadge status={policy.thisMonth as Status} />
                    </div>
                    <div className="flex items-center gap-2.5 text-xs font-medium tabular-nums text-gray-600">
                      <div className="h-1.5 max-w-[220px] flex-1 overflow-hidden rounded-full bg-gray-100">
                        <div className="h-full rounded-full bg-[#4369a0]" style={{ width: `${progress.percent}%` }} />
                      </div>
                      {policy.paidMonths} / {policy.totalMonths} months
                    </div>
                  </div>
                  <ChevronDown className={cn('h-4 w-4 shrink-0 text-gray-400 transition-transform', isActive && 'rotate-180 text-[#4369a0]')} />
                </button>

                {isActive && (
                  <div className="space-y-3 border-t border-gray-100 bg-gray-50/60 p-4">
                    <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
                      {[
                        ['Total Paid', formatCurrency(policy.totalPaid)],
                        ['Due', formatCurrency(policy.amountDue)],
                        ['Ends', formatMonthKey(details?.endMonth ?? getPolicyEndMonth(policy.startMonth))],
                        ['Nominee', details?.nomineeName || '--'],
                        ['Nominee Relationship', details?.nomineeRelationship || '--'],
                        ['Card No', policy.cardNo || '--'],
                        [
                          'Next Due',
                          canPayPolicy(policy)
                            ? formatMonthKey(details?.nextDueMonth ?? addMonthsToKey(policy.startMonth, policy.paidMonths))
                            : '--',
                        ],
                      ].map(([label, value]) => (
                        <div key={label} className="min-w-0 rounded-md border border-gray-100 bg-white px-3 py-2">
                          <p className="text-xs text-gray-500">{label}</p>
                          <p className="truncate text-sm font-medium tabular-nums text-gray-900">{value}</p>
                        </div>
                      ))}
                    </div>

                    <div className="flex flex-wrap items-center justify-end gap-3 text-[13px] text-gray-600">
                      <div className="flex flex-wrap gap-2">
                        {policy.status !== 'closed' && (
                          <Button type="button" variant="outline" size="sm" className="h-8 gap-1.5 border-gray-200" onClick={() => openFor('editPolicy', policy)}>
                            <Pencil className="h-3.5 w-3.5" />
                            Edit
                          </Button>
                        )}
                        <Button type="button" variant="outline" size="sm" className="h-8 gap-1.5 border-gray-200" onClick={() => openFor('policySavings', policy)}>
                          <PiggyBank className="h-3.5 w-3.5" />
                          Savings
                        </Button>
                        <Button type="button" variant="outline" size="sm" className="h-8 gap-1.5 border-gray-200" onClick={() => openFor('policyLoan', policy)}>
                          <Wallet className="h-3.5 w-3.5" />
                          Loan
                        </Button>
                        {canPayPolicy(policy) && (
                          <Button type="button" variant="outline" size="sm" className={SOFT_BUTTON_CLASS} onClick={() => openFor('policyPayment', policy)}>
                            <IndianRupee className="h-3.5 w-3.5" />
                            Add Payment
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </DrawerSection>

      <DrawerSection
        icon={IndianRupee}
        title="Overall Summary"
        description={`Totals across all ${policies.length} ${policies.length === 1 ? 'policy' : 'policies'}`}
      >
        <SummaryStrip
          items={[
            { label: 'Total Paid', value: formatCurrency(totals.paid), tone: 'text-teal-800' },
            { label: 'Pending Amount', value: formatCurrency(totals.pending), tone: 'text-amber-800' },
            { label: 'Loan Outstanding', value: formatCurrency(totals.loanOutstanding) },
            { label: 'Paid This Month', value: `${totals.paidThisMonth} / ${livePolicies.length}` },
          ]}
        />
        {member && (
          <div
            className={cn(
              'flex flex-wrap items-center justify-between gap-3 rounded-lg border px-4 py-2.5 text-sm text-gray-700',
              balance?.balanceDue ? 'border-amber-200 bg-amber-50' : 'border-gray-200 bg-gray-50'
            )}
          >
            <span className="flex items-center gap-2">
              <Coins className="h-4 w-4 text-gray-500" />
              Member Balance
              <b className={balance?.balanceDue ? 'text-amber-900' : 'text-gray-700'}>{formatCurrency(balance?.balanceDue ?? 0)}</b>
              {Boolean(balance?.interestDue) && (
                <span className="text-xs text-gray-500">· incl. {formatCurrency(balance?.interestDue ?? 0)} interest</span>
              )}
            </span>
            <Button type="button" variant="outline" size="sm" className="h-8 border-gray-200 bg-white" onClick={() => openDrawer('memberBalance', member)}>
              View Balance
            </Button>
          </div>
        )}
      </DrawerSection>

      {!activeId ? (
        <DrawerSection icon={CreditCard} title="Payment History">
          <p className="text-sm text-gray-500">Select a policy to see its payments.</p>
        </DrawerSection>
      ) : (
        <HistoryCard
          icon={CreditCard}
          title="Payment History"
          subtitle={member && `${memberHeading(member)} · ${activePolicyNo ?? ''}`}
          titleExtra={
            // Which policy these payments belong to; pick another to see its history.
            <Select value={activeId} onValueChange={setActiveId}>
              <SelectTrigger
                size="sm"
                aria-label="Policy for payment history"
                className="!h-6 w-auto gap-1 rounded-md border-[#4369a0]/30 bg-[#4369a0]/10 px-2 py-0 text-xs font-semibold text-[#4369a0] [&_svg]:!text-[#4369a0]"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent align="start">
                {policies.map((policy) => (
                  <SelectItem key={policy.id} value={policy.id}>
                    {policy.policyNo}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          }
          tabs={[
            {
              id: 'payments',
              label: 'Payment History',
              empty: paymentsLoading ? 'Loading payment history...' : 'No payments added yet.',
              columns: [{ label: 'Paid On' }, { label: 'Policy' }, { label: 'Period' }, { label: 'Mode' }, { label: 'Amount', align: 'right' }],
              rows: payments.map((payment) => ({
                key: payment.id,
                cells: [
                  formatPaymentDate(payment.paymentDate),
                  <b key="policy">{payment.policyNo ? <PolicyLink member={member} policyNo={payment.policyNo} /> : activePolicyNo}</b>,
                  formatPaymentPeriod(payment),
                  <span key="mode" className="text-gray-900">{paymentTypeLabel(payment.paymentType)}</span>,
                  <b key="amount">{formatCurrency(payment.amount)}</b>,
                ],
              })),
            },
          ]}
        />
      )}
    </SideDrawer>
  );
}
